import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AiAPI , apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  Wand2, Upload, Download, Loader2, Sparkles, ImageIcon, Flame,
} from "lucide-react";

const STYLES = [
  { id: "vector", label: "Vector illustration" },
  { id: "flat", label: "Flat sticker" },
  { id: "line-art", label: "Black line art" },
  { id: "3d", label: "Stylized 3D" },
  { id: "vintage", label: "Vintage screen print" },
];

const EXAMPLES = [
  "vintage racing tiger with checkered flag, screen-print style",
  "skateboarding astronaut, bold outlines, retro palette",
  "mountain sunrise line-art badge with sunburst",
  "cyber wolf head, neon accents, transparent background",
  "grunge coffee cup with steam, black and cream palette",
  "70s surf van illustration, sunset stripes",
];

function fileToBase64(file) {
  return new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res((fr.result || "").toString().split(",")[1]);
    fr.onerror = rej;
    fr.readAsDataURL(file);
  });
}

function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement("a");
  a.href = dataUrl; a.download = filename; a.click();
}

export default function AiVectorStudioPage() {
  const { user, usage, refreshUsage } = useAuth();
  const { toast } = useToast();
  const [tab, setTab] = React.useState("prompt");
  const [prompt, setPrompt] = React.useState("");
  const [style, setStyle] = React.useState("vector");
  const [dtf, setDtf] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null); // dataUrl

  const [uploadFile, setUploadFile] = React.useState(null);
  const [uploadPreview, setUploadPreview] = React.useState(null);
  const [editPrompt, setEditPrompt] = React.useState("");

  const requireAuth = () => {
    if (!user) { toast({ title: "Sign in first", description: "Create a free account to use AI tools." }); return false; }
    return true;
  };

  const runGenerate = async () => {
    if (!requireAuth()) return;
    if (!prompt.trim()) { toast({ title: "Add a prompt", variant: "destructive" }); return; }
    setBusy(true); setResult(null);
    try {
      const r = await AiAPI.generate(prompt.trim(), style, dtf);
      const dataUrl = `data:${r.data.mime_type};base64,${r.data.image_base64}`;
      setResult(dataUrl);
      await refreshUsage();
      toast({ title: "Vector generated" });
    } catch (err) {
      toast({ title: "Generation failed", description: apiErrorMessage(err, ""), variant: "destructive" });
    } finally { setBusy(false); }
  };

  const onUpload = async (file) => {
    if (!file) return;
    setUploadFile(file);
    const b64 = await fileToBase64(file);
    setUploadPreview(`data:${file.type};base64,${b64}`);
  };

  const runEdit = async () => {
    if (!requireAuth()) return;
    if (!uploadFile) { toast({ title: "Upload an image", variant: "destructive" }); return; }
    setBusy(true); setResult(null);
    try {
      const b64 = await fileToBase64(uploadFile);
      const r = await AiAPI.edit(b64, editPrompt || "Clean and vectorize for DTF printing.", dtf);
      const dataUrl = `data:${r.data.mime_type};base64,${r.data.image_base64}`;
      setResult(dataUrl);
      await refreshUsage();
      toast({ title: "Image cleaned" });
    } catch (err) {
      toast({ title: "Edit failed", description: apiErrorMessage(err, ""), variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <div className="grain">
      <Navbar />
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="chip"><Wand2 className="h-3 w-3 text-[#4aa4ff]" /> AI Vector Creator</span>
            <h1 className="display-font mt-3 text-3xl font-bold sm:text-4xl">Print-ready graphics from <span className="ember-text">a prompt or a photo</span>.</h1>
            <p className="mt-1 text-sm text-muted-foreground">Powered by Gemini Nano Banana · auto-DTF optimize on every image (Pro)</p>
          </div>
          {usage && (
            <div className="chip"><span className="h-1.5 w-1.5 rounded-full bg-[#4aa4ff]" /> {usage.pro ? "Pro · Unlimited" : `${usage.remaining} left this month`}</div>
          )}
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[380px_1fr]">
          <aside className="rounded-2xl border border-border bg-card p-5">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid w-full grid-cols-2 bg-secondary/50">
                <TabsTrigger value="prompt">Text prompt</TabsTrigger>
                <TabsTrigger value="upload">Upload photo</TabsTrigger>
              </TabsList>

              <TabsContent value="prompt" className="mt-4">
                <Label>Prompt</Label>
                <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Describe your artwork… e.g. 'vintage racing tiger with checkered flag, screen-print style'" rows={5} className="mt-1" />
                <div className="mt-3 flex flex-wrap gap-2">
                  {EXAMPLES.map((ex) => (
                    <button key={ex} onClick={() => setPrompt(ex)} className="rounded-full border border-border bg-secondary/40 px-3 py-1 text-xs text-muted-foreground hover:text-foreground">{ex}</button>
                  ))}
                </div>

                <div className="mt-5">
                  <Label>Style</Label>
                  <Select value={style} onValueChange={setStyle}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STYLES.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="mt-5 flex items-center justify-between rounded-lg border border-border bg-secondary/40 p-3">
                  <div>
                    <div className="text-sm font-medium">Auto DTF/DTG optimize</div>
                    <div className="text-xs text-muted-foreground">Transparent bg, sharp edges, print-ready</div>
                  </div>
                  <Switch checked={dtf} onCheckedChange={setDtf} />
                </div>

                <Button onClick={runGenerate} disabled={busy} className="btn-ember mt-5 h-11 w-full gap-2 font-semibold">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  Generate vector graphic
                </Button>
              </TabsContent>

              <TabsContent value="upload" className="mt-4">
                <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-border bg-secondary/30 p-6 text-center hover:border-[#4aa4ff]/60">
                  {uploadPreview ? (
                    <img src={uploadPreview} alt="upload" className="max-h-40 rounded-md object-contain" />
                  ) : (
                    <>
                      <ImageIcon className="h-8 w-8 text-[#4aa4ff]" />
                      <div className="mt-2 text-sm font-medium">Drop or choose an image</div>
                      <div className="text-xs text-muted-foreground">PNG or JPG, any resolution</div>
                    </>
                  )}
                  <input type="file" accept="image/*" hidden onChange={(e) => onUpload(e.target.files?.[0])} />
                </label>
                <Label className="mt-5 block">Instructions (optional)</Label>
                <Textarea rows={3} value={editPrompt} onChange={(e) => setEditPrompt(e.target.value)} placeholder="e.g. remove background, sharpen edges, boost saturation" className="mt-1" />
                <div className="mt-4 flex items-center justify-between rounded-lg border border-border bg-secondary/40 p-3">
                  <div>
                    <div className="text-sm font-medium">Auto DTF/DTG optimize</div>
                    <div className="text-xs text-muted-foreground">Print-ready every time</div>
                  </div>
                  <Switch checked={dtf} onCheckedChange={setDtf} />
                </div>
                <Button onClick={runEdit} disabled={busy} className="btn-ember mt-5 h-11 w-full gap-2 font-semibold">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  Clean & vectorize
                </Button>
              </TabsContent>
            </Tabs>

            <div className="mt-5 flex items-center gap-2 rounded-lg border border-border p-3 text-xs text-muted-foreground">
              <Flame className="h-4 w-4 text-[#4aa4ff]" />
              Free tier: each AI job counts as one of your 3 monthly exports.
            </div>
          </aside>

          <main>
            <div className="rounded-2xl border border-border bg-white p-6">
              {busy ? (
                <div className="flex min-h-[420px] flex-col items-center justify-center gap-3 text-muted-foreground">
                  <Loader2 className="h-6 w-6 animate-spin text-[#4aa4ff]" />
                  <div className="display-font">Forging your graphic…</div>
                </div>
              ) : result ? (
                <img src={result} alt="result" className="mx-auto max-h-[560px] w-auto max-w-full rounded-lg" />
              ) : (
                <div className="flex min-h-[420px] flex-col items-center justify-center gap-2 text-center text-muted-foreground">
                  <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary text-[#4aa4ff]"><Sparkles className="h-6 w-6" /></div>
                  <div className="display-font text-lg font-semibold text-foreground">Your artwork will appear here</div>
                  <div className="text-sm">Type a prompt or upload a photo to get started.</div>
                </div>
              )}
            </div>
            {result && (
              <div className="mt-4 flex flex-wrap gap-3">
                <Button className="btn-ember h-11 gap-2 font-semibold" onClick={() => downloadDataUrl(result, `vector_${Date.now()}.png`)}>
                  <Download className="h-4 w-4" /> Download PNG
                </Button>
                <Button variant="outline" onClick={() => setResult(null)} className="gap-2"><Sparkles className="h-4 w-4" /> Run again</Button>
              </div>
            )}
          </main>
        </div>
      </section>
      <Footer />
    </div>
  );
}
